import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Select, message, Alert } from 'antd';
import Button from '../../UI/buttons/Button';
import {
    getDemoPreflight,
    getDemoStatus,
    createDemoData,
    flushDemoData,
    DemoJob,
    DemoBatchInfo,
} from '../../api/demo';

const { Option } = Select;

// Styled components reused from the old DemoDataSettings page
const JobSelectWrapper = styled.div`
  margin: 1rem 0 2rem 0;
`;

const JobSelectLabel = styled.p`
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    min-height: 40px;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
  }
`;

const ActionsRow = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
`;

const DemoInfoBox = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  background: #f6f8fa;
  border-radius: 0.5rem;
  border-left: 4px solid #0969da;
`;

const DemoDataSection: React.FC = () => {
    const { t } = useTranslation();

    const [jobs, setJobs] = useState<DemoJob[]>([]);
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    const [hasDemoData, setHasDemoData] = useState(false);
    const [demoBatchInfo, setDemoBatchInfo] = useState<DemoBatchInfo | null>(null);
    const [canCreateDemo, setCanCreateDemo] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const refreshStatus = useCallback(async () => {
        const status = await getDemoStatus();
        if (status) {
            setHasDemoData(status.has_demo_data);
            // Update demoBatchInfo if demo data exists
            if (status.has_demo_data && status.batches.length > 0) {
                const batch = status.batches[0]; // Use the first batch
                setDemoBatchInfo({
                    id: batch.id,
                    created_at: batch.created_at,
                    created_by: batch.created_by,
                    object_count: batch.object_count,
                });
            } else {
                setDemoBatchInfo(null);
            }
        } else {
            message.error(t('ERROR_LOADING_DATA', 'Error loading data'));
        }
    }, [t]);

    const loadPreflight = useCallback(async () => {
        try {
            const data = await getDemoPreflight();
            setJobs(data.available_jobs);
            setCanCreateDemo(data.can_create_demo);
            setHasDemoData(data.has_demo_data);
            setDemoBatchInfo(data.demo_batch_info);
        } catch (err) {
            console.error(err);
            message.error(t('ERROR_LOADING_DATA', 'Error loading data'));
        }
    }, [t]);

    useEffect(() => {
        refreshStatus();
        loadPreflight();
    }, [refreshStatus, loadPreflight]);

    const handleJobSelect = (value: any) => {
        setSelectedJobId(value || '');
    };

    const handleSeed = async () => {
        if (hasDemoData) {
            message.error(
                t(
                    'DEMO_DATA_EXISTS_ERROR',
                    'Demo data already exists. Please remove existing demo data first.'
                )
            );
            return;
        }

        if (!selectedJobId) {
            message.error(t('PLEASE_SELECT_JOB', 'Please select a job first.'));
            return;
        }

        setIsCreating(true);
        setLoading(true);
        try {
            await createDemoData({ job_ids: [selectedJobId] });
            message.success(t('DEMO_DATA_CREATION_STARTED', 'Demo data creation started'));
            setSelectedJobId('');

            // Allow async task to finish then refresh
            setTimeout(async () => {
                await refreshStatus();
                await loadPreflight();
                setIsCreating(false);
            }, 3000); // Give more time for the task to complete
        } catch (err: any) {
            console.error(err);
            const errorMessage =
                err.response?.data?.error ||
                t('ERROR_CREATING_DEMO_DATA', 'Error creating demo data');
            message.error(errorMessage);
            setIsCreating(false);
        } finally {
            setLoading(false);
        }
    };

    const handleFlush = async () => {
        setLoading(true);
        try {
            await flushDemoData();
            message.success(t('DEMO_DATA_REMOVAL_STARTED', 'Demo data removal started'));

            // Allow async task to finish then refresh
            setTimeout(async () => {
                await refreshStatus();
                await loadPreflight();
            }, 1000);
        } catch (err: any) {
            console.error(err);
            const errorMessage =
                err.response?.data?.error ||
                t('ERROR_REMOVING_DEMO_DATA', 'Error removing demo data');
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h3>{t('DEMO_DATA_TITLE', 'Demo Data')}</h3>
            <p>
                {t(
                    'DEMO_DATA_DESCRIPTION',
                    'Generate realistic demo data to explore the platform features. This will create sample recruiters, candidates, and conversations.'
                )}
            </p>

            {/* Demo data info if it exists */}
            {hasDemoData && demoBatchInfo && (
                <DemoInfoBox>
                    <strong>{t('DEMO_DATA_ACTIVE', 'Demo data is active')}</strong>
                    <br />
                    {t('CREATED_AT', 'Created')}: {demoBatchInfo.created_at ? new Date(demoBatchInfo.created_at).toLocaleString() : 'Unknown'}
                    <br />
                    {t('CREATED_BY', 'Created by')}: {demoBatchInfo.created_by}
                    <br />
                    {t('OBJECTS_COUNT', 'Objects')}: {demoBatchInfo.object_count}
                </DemoInfoBox>
            )}

            {/* Job selection or alerts */}
            {jobs.length === 0 ? (
                <Alert
                    type="warning"
                    message={t('NO_ATS_JOBS_TITLE', 'No ATS Jobs Available')}
                    description={t(
                        'NO_ATS_JOBS_DESCRIPTION',
                        'No ATS jobs available. Create at least one job in your ATS first.'
                    )}
                    style={{ margin: '1rem 0' }}
                />
            ) : !canCreateDemo && !hasDemoData ? (
                <Alert
                    type="warning"
                    message={t(
                        'CANNOT_CREATE_DEMO_TITLE',
                        'Cannot Create Demo Data'
                    )}
                    description={t(
                        'CANNOT_CREATE_DEMO_DESCRIPTION',
                        'Unable to create demo data. Please check your ATS integration and ensure you have available jobs.'
                    )}
                    style={{ margin: '1rem 0' }}
                />
            ) : (
                <JobSelectWrapper>
                    <JobSelectLabel>
                        {t(
                            'SELECT_JOB_FOR_DEMO',
                            'Select the job you want demo candidates for:'
                        )}
                    </JobSelectLabel>
                    <StyledSelect
                        placeholder={t('CHOOSE_A_JOB', 'Choose a job...')}
                        value={selectedJobId || undefined}
                        onChange={handleJobSelect}
                        disabled={hasDemoData || isCreating}
                        size="large"
                    >
                        {jobs.map((job) => (
                            <Option key={job.id} value={job.id}>
                                {job.name}
                                {job.location ? ` (${job.location})` : ''}
                            </Option>
                        ))}
                    </StyledSelect>
                    {!hasDemoData && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                            {t(
                                'SINGLE_JOB_RECOMMENDATION',
                                'For the best demo experience, we recommend selecting just one job. The system will create a comprehensive ecosystem with 6 specialized recruiters and 18 candidates across all employment types.'
                            )}
                        </div>
                    )}
                </JobSelectWrapper>
            )}

            {/* Action buttons */}
            <ActionsRow>
                <Button
                    type="primary"
                    disabled={
                        !canCreateDemo ||
                        !selectedJobId ||
                        loading ||
                        hasDemoData ||
                        isCreating
                    }
                    loading={loading || isCreating}
                    onClick={handleSeed}
                >
                    {isCreating
                        ? t('CREATING_DEMO_DATA', 'Creating demo data...')
                        : t('GENERATE_DEMO_DATA', 'Generate demo data')
                    }
                </Button>
                <Button
                    danger
                    disabled={!hasDemoData || loading || isCreating}
                    loading={loading}
                    onClick={handleFlush}
                >
                    {t('REMOVE_DEMO_DATA', 'Remove demo data')}
                </Button>
            </ActionsRow>
        </>
    );
};

export default DemoDataSection; 